import { Group, Stack, TextInput } from "@mantine/core"
import { useDebouncedValue } from "@mantine/hooks"
import {
  IconCertificate,
  IconHeartHandshake,
  IconSearch,
} from "@tabler/icons-react"
import { Select } from "@tfg/ui"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import {
  supplierRiskFiltersSchema,
  supplierRiskFiltersValidationType,
} from "@tfg/types/schemas/organization/schemas"

import { DataFetchWrapper } from "~/components/DataFetchWrapper"
import { usePagination } from "~/components/hooks/usePagination"
import { i18n } from "~/i18n.ts"
import { SuppliersRisk } from "~/pages/Brand/Suppliers/List/SupplierRisk/SupplierRisk.tsx"
import { useListSuppliersRiskQuery } from "~/pages/Brand/Suppliers/List/supplierHooks.ts"

const RISK_OPTIONS = [
  { label: i18n.t("suppliers:filter.risk.high"), value: "HIGH" },
  { label: i18n.t("suppliers:filter.risk.medium"), value: "MEDIUM" },
  { label: i18n.t("suppliers:filter.risk.low"), value: "LOW" },
]

export function SupplierRiskWrapper() {
  const { t } = useTranslation("suppliers")
  const { cursorBasedPagination, pageBasedPagination, setPageBasedPagination } =
    usePagination()
  const [searchSupplier, setSearchSupplier] = useState<string>("")
  const [debouncedSupplier] = useDebouncedValue(searchSupplier, 600)
  const [filterValues, setFilterValues] = useState<
    Partial<supplierRiskFiltersValidationType>
  >({})
  const suppliersRiskQuery = useListSuppliersRiskQuery({
    fetchDataOptions: cursorBasedPagination,
    searchSupplier: debouncedSupplier,
    filters: filterValues,
  })

  useEffect(() => {
    setPageBasedPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }))
  }, [filterValues, debouncedSupplier, setPageBasedPagination])

  return (
    <Stack>
      <Group justify="apart">
        <Group>
          <TextInput
            value={searchSupplier}
            autoFocus
            onChange={(e) => setSearchSupplier(e.target.value)}
            w={200}
            size="xs"
            leftSection={<IconSearch size={16} />}
            placeholder={t("list.searchPlaceholder")}
          />
          <Select
            w={150}
            size="xs"
            leftSection={<IconHeartHandshake size={16} />}
            data={RISK_OPTIONS}
            value={filterValues.riskCollaboration ?? null}
            placeholder={t("list.riskPlaceholder")}
            clearable
            onChange={(value) => {
              setFilterValues(
                supplierRiskFiltersSchema.parse({
                  ...filterValues,
                  riskCollaboration: value ?? undefined,
                })
              )
            }}
          />
          <Select
            w={150}
            size="xs"
            leftSection={<IconCertificate size={16} />}
            data={RISK_OPTIONS}
            value={filterValues.riskCertification ?? null}
            placeholder={t("list.riskPlaceholder")}
            clearable
            onChange={(value) => {
              setFilterValues(
                supplierRiskFiltersSchema.parse({
                  ...filterValues,
                  riskCertification: value ?? undefined,
                })
              )
            }}
          />
        </Group>
      </Group>
      <DataFetchWrapper query={suppliersRiskQuery}>
        {({ data }) => (
          <SuppliersRisk
            suppliersData={data}
            pagination={pageBasedPagination}
            setPagination={setPageBasedPagination}
          />
        )}
      </DataFetchWrapper>
    </Stack>
  )
}
